import { DeviceType, IDisplayItem, IScreenDesignDictionary } from "@kuika/kuika-cl-model";
import { IScreenDesignContextState } from "../../contexts/screen-design-context/screen-design-context";
import type { ScreenDesignInstance } from "./designer-context";
import { getValues } from "./designer-context";
import { safeParse } from "./designer-utils";

/** Runtime-only interface for dragDrop methods used in component actions. Not part of IScreenDesignContextState. */
interface DragDropLike {
  setColumnsNumber: (
    fractions: number[],
    kuikaId: string,
    deviceType: DeviceType,
    cascadeTablet: boolean,
    cascadePhone: boolean
  ) => void;
}

/** Access the dragDrop instance that exists at runtime on the provider but is not on IScreenDesignContextState. */
function getDragDrop(sdInstance: ScreenDesignInstance): DragDropLike | undefined {
  return (sdInstance as unknown as { dragDrop?: DragDropLike }).dragDrop;
}

/** Access gridSystemCount from the provider's state or project. */
function getGridSystemCount(sd: IScreenDesignContextState): number {
  const fromState = (sd as unknown as { state?: { project?: { componentSetObj?: { gridSystemCount?: number } } } })
    .state;
  return fromState?.project?.componentSetObj?.gridSystemCount || sd.project?.componentSetObj?.gridSystemCount || 24;
}

export function injectIntoDesignerDictionary(di: IDisplayItem, dict: IScreenDesignDictionary): void {
  if (di && di.id != null) {
    dict[di.id] = di;
  }
  if (di?.children) {
    for (const child of di.children) {
      injectIntoDesignerDictionary(child, dict);
    }
  }
}

export function applyDesignerRowColumns(
  sdInstance: ScreenDesignInstance,
  newChild: IDisplayItem,
  newChildId: string,
  columnsParam: unknown,
  tabletColumnsParam?: number,
  mobileColumnsParam?: number
): void {
  const sd: IScreenDesignContextState = getValues(sdInstance);
  const dragDrop = getDragDrop(sdInstance);
  if (!dragDrop || !newChildId) return;

  const gridSystemCount = getGridSystemCount(sd);
  const columns = safeParse(columnsParam);

  const hasTablet = typeof tabletColumnsParam === "number" && tabletColumnsParam >= 1;
  const hasMobile = typeof mobileColumnsParam === "number" && mobileColumnsParam >= 1;

  if (columns && Array.isArray(columns) && columns.length > 0) {
    const fractions = (columns as number[]).map((column: number) => column / gridSystemCount);
    dragDrop.setColumnsNumber(fractions, newChildId, DeviceType.Desktop, !hasTablet, !hasMobile);
  } else {
    // A bare count (markup `<Row>` with N `<Col>` and no `columns=`) → N equal slots.
    const requestedColCount: number =
      typeof columns === "number" && Number.isInteger(columns) && columns >= 1 ? columns : 0;
    const defaultColCount = requestedColCount || newChild.children?.length || 3;
    const fractions = Array(defaultColCount).fill(1 / defaultColCount) as number[];
    dragDrop.setColumnsNumber(fractions, newChildId, DeviceType.Desktop, !hasTablet, !hasMobile);
  }

  if (hasTablet) {
    const colCount = newChild.children?.length || 3;
    const tabletFractions = Array(colCount).fill(1 / tabletColumnsParam!) as number[];
    dragDrop.setColumnsNumber(tabletFractions, newChildId, DeviceType.Tablet, false, !hasMobile);
  }

  if (hasMobile) {
    const colCount = newChild.children?.length || 3;
    const mobileFractions = Array(colCount).fill(1 / mobileColumnsParam!) as number[];
    dragDrop.setColumnsNumber(mobileFractions, newChildId, DeviceType.Phone, false, false);
  }
}
